import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { env } from '@/lib/env';

export type EnvironmentName = 'localhost' | 'staging' | 'production';

export interface EnvironmentConfig {
  name: EnvironmentName;
  label: string;
  apiUrl: string;
  color: 'blue' | 'amber' | 'red';
}

interface EnvironmentContextValue {
  current: EnvironmentConfig;
  environments: EnvironmentConfig[];
  switchEnvironment: (name: EnvironmentName) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue | null>(null);

export function EnvironmentProvider({ children }: { children: React.ReactNode }) {
  const environments: EnvironmentConfig[] = useMemo(() => [
    {
      name: 'localhost' as const,
      label: 'Localhost',
      apiUrl: env('VITE_LOCAL_API_URL') || 'http://localhost:5001',
      color: 'blue',
    },
    {
      name: 'staging' as const,
      label: 'Staging',
      apiUrl: env('VITE_STAGING_API_URL') || 'http://localhost:5001',
      color: 'amber',
    },
    {
      name: 'production' as const,
      label: 'Production',
      apiUrl: env('VITE_PROD_API_URL') || 'http://localhost:5001',
      color: 'red',
    },
  ], []);

  const defaultEnv = (env('VITE_DEFAULT_ENV') || 'localhost') as EnvironmentName;
  const [currentName, setCurrentName] = useState<EnvironmentName>(defaultEnv);

  const switchEnvironment = useCallback((name: EnvironmentName) => {
    setCurrentName(name);
    localStorage.setItem('admin_last_env', name);
  }, []);

  const current = environments.find((e) => e.name === currentName) || environments[0];

  return (
    <EnvironmentContext.Provider value={{ current, environments, switchEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const context = useContext(EnvironmentContext);
  if (!context) throw new Error('useEnvironment must be used within EnvironmentProvider');
  return context;
}
